import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  getStockCountItemDisplayName,
  parseAdjustmentInput,
  validateAdjustmentInput,
} from "../utils/stockCountItemModel";

export default function EditStockCountAdjustmentDialog({
  open,
  item,
  loading = false,
  error = "",
  onClose,
  onSave,
}) {
  const [value, setValue] = useState("");
  const validationError = validateAdjustmentInput(value);
  const itemName = item ? getStockCountItemDisplayName(item.itemSnapshot) : "";
  const countUnit = item?.countUnitSnapshot || "";

  useEffect(() => {
    if (!open || !item) {
      setValue("");
      return;
    }
    const current =
      item.manualAdjustmentQuantity ?? item.finalAdjustmentQuantity ?? "";
    setValue(current === null ? "" : String(current));
  }, [item, open]);

  function handleClose() {
    if (!loading) onClose?.();
  }

  function handleSave() {
    if (validationError || !item) return;
    onSave?.(item, parseAdjustmentInput(value));
  }

  function handleUseSuggested() {
    if (!item || loading) return;
    onSave?.(item, null);
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={loading ? undefined : handleClose}
      PaperProps={{ sx: { borderRadius: "24px", maxWidth: 460 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: "primary.900", pb: 1 }}>
        Editar ajuste
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 700 }}>
            {itemName}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <TextField
              label="Ajuste manual"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={loading}
              error={Boolean(validationError)}
              helperText={validationError || "Usá hasta 2 decimales."}
              fullWidth
              size="small"
              inputProps={{ inputMode: "decimal" }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "14px" },
              }}
            />
            {countUnit ? (
              <Typography
                aria-label="Unidad de conteo"
                sx={{
                  minWidth: 56,
                  pt: 1.1,
                  color: "primary.900",
                  fontSize: 14,
                  fontWeight: 800,
                }}
              >
                {countUnit}
              </Typography>
            ) : null}
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: "14px" }}>
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button
          type="button"
          onClick={handleUseSuggested}
          disabled={loading}
          sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
        >
          Usar sugerido
        </Button>
        <Button
          type="button"
          onClick={handleClose}
          disabled={loading}
          sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={loading || Boolean(validationError)}
          onClick={handleSave}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
        >
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
